class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.exitAnimation = null;
    this.genieLayer = null;
    this.onOpenTransitionEnd = null;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    if (this.exitAnimation) {
      this.exitAnimation.cancel();
      this.exitAnimation = null;
      this.genieLayer?.remove();
      this.genieLayer = null;
    }

    if (this.onOpenTransitionEnd) {
      this.notification.removeEventListener('transitionend', this.onOpenTransitionEnd);
      this.onOpenTransitionEnd = null;
    }

    const wasActive = this.notification.classList.contains('active');
    this.notification.classList.add('animate', 'active');

    const focusDialog = () => {
      if (!this.notification.classList.contains('active')) return;
      this.notification.focus();
      trapFocus(this.notification);
    };

    if (wasActive || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      focusDialog();
    } else {
      const onOpenTransitionEnd = (event) => {
        if (event.target !== this.notification || event.propertyName !== 'transform') return;
        this.notification.removeEventListener('transitionend', onOpenTransitionEnd);
        this.onOpenTransitionEnd = null;
        focusDialog();
      };
      this.onOpenTransitionEnd = onOpenTransitionEnd;
      this.notification.addEventListener('transitionend', onOpenTransitionEnd);
    }

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    if (!this.notification.classList.contains('active') || this.exitAnimation) return;

    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus(this.activeElement);

    if (this.onOpenTransitionEnd) {
      this.notification.removeEventListener('transitionend', this.onOpenTransitionEnd);
      this.onOpenTransitionEnd = null;
    }

    const icon = document.getElementById('cart-icon-bubble');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !icon || !this.notification.animate) {
      this.notification.classList.remove('active', 'animate');
      return;
    }

    const card = this.notification.getBoundingClientRect();
    const basket = icon.getBoundingClientRect();
    if (!card.width || !card.height) {
      this.notification.classList.remove('active', 'animate');
      return;
    }

    // The real dialog lives inside the header's stacking context. Fly a
    // visual-only copy above the whole page so the menu cannot cover it.
    const layer = document.createElement('div');
    layer.className = 'cart-notification-wrapper quive-cart-genie-layer';
    layer.setAttribute('aria-hidden', 'true');
    layer.setAttribute('inert', '');

    const ghost = this.notification.cloneNode(true);
    ghost.removeAttribute('id');
    ghost.removeAttribute('role');
    ghost.removeAttribute('aria-modal');
    ghost.removeAttribute('tabindex');
    ghost.classList.remove('animate', 'focused');
    ghost.classList.add('active', 'quive-cart-genie-ghost');
    ghost.querySelectorAll('[id]').forEach((element) => {
      element.dataset.genieId = element.id;
      element.removeAttribute('id');
    });
    ghost.style.left = `${card.left}px`;
    ghost.style.top = `${card.top}px`;
    ghost.style.right = 'auto';
    ghost.style.width = `${card.width}px`;
    ghost.style.maxWidth = 'none';
    ghost.style.height = `${card.height}px`;
    layer.appendChild(ghost);
    document.body.appendChild(layer);
    this.genieLayer = layer;
    this.notification.classList.remove('active', 'animate');

    const endScaleX = .055;
    const endScaleY = .035;
    const x = basket.left + basket.width / 2 - card.right + card.width * endScaleX / 2;
    const y = basket.top + basket.height / 2 - card.top - card.height * endScaleY / 2;

    const animation = ghost.animate(
      [
        { transform: 'translate3d(0, 0, 0) scale(1) skewX(0)', opacity: 1, borderRadius: '2rem', offset: 0 },
        { transform: `translate3d(${x * .13}px, ${y * .06}px, 0) scale(.96, .84) skewX(-4deg)`, opacity: 1, borderRadius: '2.8rem', offset: .28 },
        { transform: `translate3d(${x * .55}px, ${y * .43}px, 0) scale(.62, .38) skewX(-13deg)`, opacity: .96, borderRadius: '3.8rem', offset: .7 },
        { transform: `translate3d(${x}px, ${y}px, 0) scale(${endScaleX}, ${endScaleY}) skewX(-22deg)`, opacity: 0, borderRadius: '50%', offset: 1 },
      ],
      { duration: 1300, easing: 'cubic-bezier(.25, .65, .34, 1)', fill: 'forwards' }
    );

    this.exitAnimation = animation;
    animation.finished.then(() => {
      if (this.exitAnimation !== animation) return;
      layer.remove();
      this.genieLayer = null;
      animation.cancel();
      this.exitAnimation = null;
      icon.classList.add('quive-cart-received');
      window.setTimeout(() => icon.classList.remove('quive-cart-received'), 900);
    }).catch(() => {});
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
